import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Copy } from 'lucide-react';
import { Modal } from '@/components';
import { Column, Row, FormField } from '@/components/SettingsLayout';
import { Button, Input, Text, CheckBox } from '@/ui';
import { useCreateApiKeyMutation } from '@/store/slices/orgApiKeySlice';
import { Permissions } from '@/services/ApiKeys/org-api-key.service';
import { maskApiKey, copyToClipboard } from '@/utils/helpers/apiKeyHelpers';

type FormValues = {
  keyName: string;
  permissions: string[];
};

function AddApiKeyModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { id: organizationId } = useParams();
  const [searchParams] = useSearchParams();
  const open = searchParams.get('mode') === 'new-api-key';

  const [createApiKey, { isLoading, reset: resetMutation }] = useCreateApiKeyMutation();
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      keyName: t('ApiKeys.default_key_name'),
      permissions: Object.values(Permissions),
    },
  });

  const permissions = watch('permissions');

  const closeModal = () => {
    navigate(pathname);
  };

  useEffect(() => {
    if (!open) {
      reset({
        keyName: t('ApiKeys.default_key_name'),
        permissions: Object.values(Permissions),
      });
      setCreatedKey(null);
      resetMutation();
    }
  }, [open, reset, resetMutation, t]);

  const handlePermissionToggle = (permission: string) => {
    const current = permissions || [];
    if (current.includes(permission)) {
      setValue(
        'permissions',
        current.filter((p) => p !== permission),
      );
    } else {
      setValue('permissions', [...current, permission]);
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!organizationId) return;

    try {
      const key = await createApiKey({
        organizationId,
        name: data.keyName,
        permissions: data.permissions,
      }).unwrap();
      setCreatedKey(key);
      toast.success(t('ApiKeys.create_success'));
    } catch (error) {
      console.error('Failed to create API key', error);
      toast.error(t('ApiKeys.create_error'));
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey) return;
    try {
      await copyToClipboard(createdKey);
      toast.success(t('ApiKeys.copy_success'));
    } catch {
      toast.error(t('ApiKeys.copy_error'));
    }
  };

  if (createdKey) {
    return (
      <Modal open={open} closeModal={closeModal} fitContent>
        <div className='w-[500px]'>
          <Modal.Title title={t('ApiKeys.key_created_title')} />
          <Modal.Content border>
            <Column gapSize='16'>
              <Text size='s' color='base-grey'>
                {t('ApiKeys.key_created_warning')}
              </Text>
              <div className='flex items-center gap-2 p-3 bg-muted rounded-md'>
                <div className='flex-1'>
                  <Input value={maskApiKey(createdKey)} readOnly variant='rowInput' />
                </div>
                <Button variant='outline' onClick={handleCopyKey} icon testId='copy-api-key'>
                  <Copy className='h-4 w-4' />
                  {t('ApiKeys.copy')}
                </Button>
              </div>
            </Column>
          </Modal.Content>
          <Modal.Footer justify='flex-end'>
            <Button variant='primary' onClick={closeModal} testId='close-api-key-modal'>
              {t('ModalBuilder.confirm')}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} closeModal={closeModal} fitContent>
      <form onSubmit={handleSubmit(onSubmit)} className='w-[500px]'>
        <Modal.Title title={t('ApiKeys.create_title')} />
        <Modal.Content border>
          <Column gapSize='16'>
            <FormField className='grid'>
              <label htmlFor='keyName'>
                <Text size='s' weight='light-medium'>
                  {t('ApiKeys.key_name_label')}
                </Text>
              </label>
              <Controller
                name='keyName'
                control={control}
                rules={{ required: t('ApiKeys.key_name_required') }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id='keyName'
                    placeholder={t('ApiKeys.key_name_placeholder')}
                    isError={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    sanitize={false}
                  />
                )}
              />
            </FormField>

            <Column gapSize='8'>
              <Text size='s' weight='semi-bold'>
                {t('ApiKeys.permissions_label')}
              </Text>
              <div className='flex flex-col gap-3 p-3 border rounded-md'>
                {Object.values(Permissions).map((permission) => (
                  <CheckBox
                    key={permission}
                    id={`permission-${permission}`}
                    label={t(`ApiKeys.permission_${permission}`)}
                    isChecked={permissions?.includes(permission) ?? false}
                    onChange={() => handlePermissionToggle(permission)}
                  />
                ))}
              </div>
            </Column>
          </Column>
        </Modal.Content>
        <Modal.Footer justify='flex-end'>
          <Row gapSize='8' fullWidth={false}>
            <Button
              variant='outline'
              onClick={closeModal}
              type='button'
              disabled={isLoading}
              testId='cancel-api-key'
            >
              {t('Shared.cancel')}
            </Button>
            <Button
              variant='primary'
              type='submit'
              disabled={isLoading || !permissions?.length}
              testId='create-api-key'
            >
              {t('ApiKeys.create')}
            </Button>
          </Row>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default AddApiKeyModal;
